<template>
  <div
    v-if="template == 0"
    class="group bg-white border border-border rounded-lg hover:shadow-lg hover:border-primary/40 transition-all duration-300 flex flex-col md:flex-row overflow-hidden relative"
  >
    <a
      :href="currentLink"
      class="relative w-full md:w-48 h-56 flex-shrink-0 bg-surface border-r border-border p-4 flex items-center justify-center"
    >
      <div class="absolute top-2 left-2 z-10">
        <span
          class="bg-white/90 backdrop-blur-sm border border-border rounded px-1.5 py-0.5 flex items-center shadow-sm"
        >
          <img
            loading="lazy"
            v-if="product.brand && product.brand.logo_image"
            :src="'/uploads/thumbnail-brands/' + product.brand.logo_image"
            class="h-4 w-auto mr-1 object-contain"
            :alt="product.brand.name"
            @error="setDefaultImage"
          />
          <span v-else class="text-[9px] font-bold text-text-main">{{
            product.brand.name
          }}</span>
        </span>
      </div>

      <img
        loading="lazy"
        :src="imageUrl"
        :alt="product.name"
        :style="vtStyle"
        class="w-full h-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500"
      />
    </a>

    <div class="flex-grow flex flex-col p-6 min-w-0">
      <div
        class="flex flex-col md:flex-row justify-between items-start gap-4 h-full"
      >
        <div class="flex-grow space-y-2">
          <a :href="currentLink" class="block">
            <h4
              class="text-lg font-bold text-text-main leading-tight group-hover:text-primary transition-colors truncate-2-lines"
            >
              {{ product.translation?.name || product.name }}
            </h4>
          </a>

          <div class="flex items-center gap-3">
            <span
              class="text-xs font-mono font-medium text-text-secondary bg-surface px-2 py-0.5 rounded"
            >
              REF: {{ displayCode }}
            </span>
          </div>

          <!-- Selectores de variantes -->
          <div
            class="flex flex-wrap gap-6 pt-4 items-center"
            v-if="hasVariants"
          >
            <div
              class="space-y-1.5"
              v-for="attribute in localAttributes"
              :key="attribute.id"
            >
              <label
                class="text-[10px] font-bold text-text-secondary uppercase tracking-wider"
              >
                {{ attribute.name }}
              </label>

              <!-- SELECT para todo excepto color -->
              <select
                v-if="attribute.type === 'select'"
                @change="
                  onAttributeChange(
                    attribute,
                    attribute.options.find(
                      (o) => o.value_id == $event.target.value,
                    ),
                  )
                "
                :value="attribute.selected?.value_id"
                class="block w-40 text-xs py-1.5 border border-border rounded-md bg-white focus:ring-1 focus:ring-primary focus:border-primary transition-shadow cursor-pointer"
              >
                <option
                  v-for="option in attribute.options"
                  :value="option.value_id"
                  :key="option.value_id"
                >
                  {{ option.value }}
                </option>
              </select>

              <!-- COLOR como círculos -->
              <div
                v-else-if="attribute.type === 'color'"
                class="flex gap-2 items-center h-8"
              >
                <button
                  v-for="option in attribute.options"
                  :key="option.value_id"
                  @click="onAttributeChange(attribute, option)"
                  class="w-6 h-6 rounded-full border border-border transition-all focus:outline-none"
                  :class="[
                    attribute.selected &&
                    attribute.selected.value_id === option.value_id
                      ? 'ring-2 ring-primary ring-offset-2 scale-110'
                      : 'hover:ring-2 hover:ring-gray-300 hover:ring-offset-1',
                  ]"
                  :style="{backgroundColor: option.info}"
                  :title="option.value"
                ></button>
              </div>
            </div>
          </div>

          <span
            class="inline-block mt-2 text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5"
            v-if="totalProductVariations > 1"
          >
            {{ variationLabel }} articles disp.
          </span>
        </div>

        <div
          class="w-full md:w-64 flex-shrink-0 flex flex-col md:items-end md:text-right mt-4 md:mt-0 border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-4"
        >
          <!-- Stock -->
          <div class="mb-4" v-if="currentStock">
            <div
              class="flex items-center md:justify-end gap-1.5"
              :class="currentStock.status"
            >
              <x-icons name="check_circle" class="text-[18px] fill-1" />

              <span class="text-xs font-bold uppercase">
                {{ currentStock.text_ca }}
              </span>
            </div>

            <p
              class="text-[11px] text-text-secondary mt-1"
              v-if="productUnits > 0 && currentStock.stock_web > 0"
            >
              <span class="font-bold text-text-main">
                {{ currentStock.stock_web }}
                {{ transPeralbaPro.item1180 || "unitats" }}
              </span>
            </p>

            <button
              v-if="productUnits > 0 && currentStock.stock_web > 0"
              type="button"
              class="text-[10px] text-primary hover:underline mt-1 font-medium"
              @click="openModalClickAndCollect"
            >
              {{ transPeralbaPro.item894 }}
            </button>
          </div>

          <!-- Precios -->
          <div class="space-y-0.5 mt-auto">
            <!-- Articulo Fegime de las listas MatMax "sin precio" -->
            <div class="flex flex-col" v-if="showPvpConsultar">
              <span class="text-lg font-bold text-text-main leading-none">
                PVP a consultar
              </span>
            </div>
            <template v-else-if="parseFloat(currentDiscount) > 0">
              <div class="flex items-center md:justify-end gap-2">
                <span class="text-xs text-text-secondary line-through">
                  {{ currency(currentPriceWithoutDiscount) }}
                </span>
                <span
                  class="text-[10px] font-bold text-white bg-red-500 px-1.5 py-0.5 rounded"
                >
                  -{{ percentage(currentDiscount) }}
                </span>
              </div>
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-text-secondary uppercase"
                >
                  {{ transPeralbaPro.item882 || "Oferta" }}
                </span>
                <span class="text-3xl font-black text-primary leading-none">
                  {{ currency(currentPrice) }}
                </span>
              </div>
            </template>

            <template v-else>
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold text-text-secondary uppercase"
                >
                  {{ transPeralbaPro.item132 || "PVP" }}
                </span>
                <span class="text-3xl font-black text-text-main leading-none">
                  {{ currency(currentPrice) }}
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Controles de cantidad y añadir al carrito -->
      <div class="mt-auto pt-6 flex flex-wrap justify-end items-center gap-3">
        <div
          class="flex items-center h-10 border border-border rounded-md overflow-hidden bg-surface group-focus-within:border-primary transition-colors"
        >
          <button
            @click.prevent="decUnits(product)"
            class="w-10 h-full flex items-center justify-center hover:bg-gray-200 border-r border-border transition-colors text-text-secondary"
            :class="{
              'opacity-50 cursor-not-allowed': productUnits <= minQuantity,
            }"
          >
            <x-icons name="delete" class="text-sm" />
          </button>

          <input
            type="number"
            class="w-12 h-full border-none bg-transparent text-center text-sm font-bold p-0 focus:ring-0 appearance-none"
            :class="product.minimum > 0 ? 'only-decimal' : 'only-number'"
            :min="minQuantity"
            max="10000"
            :step="stepQuantity"
            v-model.number="productUnits"
            @keyup.enter="addToShoppingCart($event)"
            @click.stop
          />

          <button
            @click.prevent="incUnits(product)"
            class="w-10 h-full flex items-center justify-center hover:bg-gray-200 border-l border-border transition-colors text-text-secondary"
          >
            <x-icons name="add" class="text-sm" />
          </button>
        </div>

        <button
          v-if="canWriteOrders"
          @click.prevent="addToShoppingCart($event)"
          :disabled="loading || showPvpConsultar"
          :title="showPvpConsultar ? 'PVP a consultar' : null"
          class="px-6 h-10 bg-primary text-white rounded-md font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary-dark transition-all shadow-sm active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-primary"
        >
          <img
            loading="lazy"
            v-if="!loading"
            src="/front/hi/cart-white.svg"
            class="w-4 h-4 invert brightness-0"
            style="filter: brightness(0) invert(1);"
          />
          <svg
            v-else
            class="animate-spin h-4 w-4 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>

          {{ transPeralbaPro.item896 }}
        </button>
      </div>
    </div>
  </div>

  <!-- TEMPLATE 1 - GRID CARD -->
  <div
    class="product-card-js w-full group bg-white border border-border rounded-lg p-2 md:p-4 hover:shadow-lg hover:border-primary/50 transition-all duration-300 relative h-full flex flex-col gap-2 md:gap-0"
    v-else
  >
    <div class="flex flex-row md:flex-col gap-4 md:gap-0 h-full">
      <div class="flex flex-col gap-2 w-[120px] md:w-full">
        <div
          class="relative w-[120px] h-[120px] md:w-full md:h-auto md:aspect-square bg-surface rounded-md md:mb-4 overflow-hidden flex items-center justify-center shrink-0"
        >
          <a
            :href="currentLink"
            class="w-full h-full flex items-center justify-center"
          >
            <img
              loading="lazy"
              class="w-3/4 h-3/4 object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-110"
              :src="imageUrl"
              :alt="product.name"
              :style="vtStyle"
              @error="setDefaultImage"
            />
          </a>
          <button
            @click="handleWishlist()"
            aria-label="Guardar en una lista"
            class="absolute top-2 left-2 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-primary hover:text-white text-text-secondary transition-colors shadow-sm z-20 backdrop-blur-sm"
          >
            <x-icons name="bookmark" class="text-[18px]" />
          </button>
          <div
            v-if="
              product.brand.logo_image && product.brand.logo_image !== 'null'
            "
            class="hidden md:flex absolute bottom-1 left-1 items-center gap-3 border border-border rounded-lg px-2 py-1 bg-white/80 backdrop-blur-sm z-10"
          >
            <img
              :src="'/uploads/thumbnail-brands/' + product.brand.logo_image"
              :alt="product.brand.name"
              loading="lazy"
              class="max-h-[20px] w-auto mix-blend-multiply"
            />
          </div>
        </div>
        <div
          v-if="product.brand.logo_image && product.brand.logo_image !== 'null'"
          class="md:hidden flex items-center gap-3 border border-border rounded-lg px-2 py-1 bg-white/80 backdrop-blur-sm"
        >
          <img
            :src="'/uploads/thumbnail-brands/' + product.brand.logo_image"
            :alt="product.brand.name"
            loading="lazy"
            class="max-h-[20px] w-auto mix-blend-multiply"
          />
        </div>
      </div>

      <div class="flex flex-col flex-1 h-full">
        <p class="text-xs text-text-secondary font-medium truncate">
          REF: {{ displayCode }}
        </p>

        <a :href="currentLink" class="md:mb-2">
          <h4
            class="text-sm font-bold text-text-main leading-snug group-hover:text-primary transition-colors min-h-[2.5em]"
            :title="displayName"
          >
            {{ displayName }}
          </h4>
        </a>

        <!-- Variantes para template grid -->
        <div class="flex gap-2 flex-wrap py-2" v-if="hasVariants">
          <div
            class="flex items-center gap-1 flex-wrap"
            v-for="attribute in localAttributes"
            :key="attribute.id"
          >
            <template v-if="attribute.type === 'select'">
              <p class="font-medium text-s">{{ attribute.name }}:</p>
              <select
                class="cursor-pointer text-xs py-1.5 pl-2 pr-6 border border-border rounded bg-surface text-text-main focus:ring-1 focus:ring-primary focus:border-primary"
                @change="
                  onAttributeChange(
                    attribute,
                    attribute.options.find(
                      (o) => o.value_id == $event.target.value,
                    ),
                  )
                "
                :value="attribute.selected?.value_id"
              >
                <option
                  v-for="option in attribute.options"
                  :value="option.value_id"
                  :key="option.value_id"
                >
                  {{ option.value }}
                </option>
              </select>
            </template>
            <template v-else-if="attribute.type === 'color'">
              <p class="font-medium text-s">{{ attribute.name }}:</p>
              <div
                class="cursor-pointer w-5 h-5 rounded-full border border-gray-300 shadow-sm"
                v-for="option in attribute.options"
                :key="option.value_id"
                :style="{'background-color': option.info}"
                :class="
                  attribute.selected &&
                  attribute.selected.value_id === option.value_id
                    ? 'ring-1 ring-offset-1 ring-primary'
                    : ''
                "
                @click="onAttributeChange(attribute, option)"
              ></div>
            </template>
          </div>
        </div>

        <div class="mt-auto flex flex-row gap-2 items-center mb-2">
          <div class="flex items-center gap-2">
            <div
              :class="{
                'bg-green-500': displayStockStatus === 'stock-success',
                'bg-amber-500':
                  displayStockStatus === 'stock-warning' ||
                  displayStockStatus === 'stock-danger',
              }"
              class="size-2.5 rounded-full animate-pulse"
            ></div>

            <span
              :class="{
                'text-green-700': displayStockStatus === 'stock-success',
                'text-amber-700':
                  displayStockStatus === 'stock-warning' ||
                  displayStockStatus === 'stock-danger',
              }"
              class="text-sm font-bold"
            >
              <template v-if="displayStockStatus === 'stock-success'">
                {{ transPeralbaPro.item1297 }}
                <template v-if="displayStockWeb <= 0">
                  {{ transPeralbaPro.item1327 }}
                </template>
              </template>

              <template v-else-if="displayStockStatus === 'stock-warning'">
                {{ transPeralbaPro.item1298 }}
                <template v-if="displayStockWeb <= 0">
                  {{ transPeralbaPro.item1327 }}
                </template>
              </template>

              <template v-else-if="displayStockStatus === 'stock-danger'">
                {{ transPeralbaPro.item1299 }}
              </template>
            </span>
          </div>

          <span
            v-if="displayStockStatus === 'stock-success' && displayStockWeb > 0"
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
            style="display: inline-flex; align-items: center; justify-content: center; padding: 0.35em 0.85em; border-radius: 9999px;font-size: 0.85rem; font-weight: 600; line-height: 1; white-space: nowrap; transition: all 0.2s ease;background-color: #ecfdf5;color: #047857; border: 1px solid transparent;"
          >
            Click&Collect
          </span>
        </div>

        <div
          class="flex items-center justify-between pt-2 border-t border-dashed border-border"
        >
          <!-- Articulo Fegime de las listas MatMax "sin precio" -->
          <div class="flex flex-col flex-1" v-if="showPvpConsultar">
            <span class="text-sm font-bold text-text-main">
              PVP a consultar
            </span>
          </div>
          <div
            class="flex flex-col flex-1 gap-1"
            v-else-if="parseFloat(currentDiscount) > 0"
          >
            <div class="flex gap-2 items-center">
              <span
                class="text-xs text-text-secondary line-through decoration-primary"
              >
                {{ currency(currentPriceWithoutDiscount) }}
              </span>
              <span
                class="bg-primary-50 text-primary text-xs font-bold px-2 py-0.5 rounded"
              >
                -{{ percentage(currentDiscount) }}
              </span>
            </div>
            <span class="text-lg font-bold text-red-600">
              {{ currency(currentPrice) }}
            </span>
          </div>
          <div class="flex flex-col flex-1" v-else>
            <span class="text-lg font-bold text-text-main">
              {{ currency(currentPrice) }}
            </span>
          </div>
        </div>

        <div class="hidden md:flex items-center justify-between gap-2">
          <div
            class="input-quantity-div flex items-center border border-border rounded-md bg-surface h-8"
          >
            <button
              @click="decUnits(product)"
              :class="
                productUnits > minQuantity ? 'text-gray-600' : 'text-gray-300'
              "
              class="btn-qty minus w-6 h-full flex items-center justify-center hover:bg-gray-200 rounded-l-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="16px"
                viewBox="0 -960 960 960"
                width="16px"
                fill="currentColor"
              >
                <path d="M200-440v-80h560v80H200Z" />
              </svg>
            </button>
            <input
              :class="product.minimum > 0 ? 'only-decimal' : 'only-number'"
              type="number"
              class="input-quantity [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none w-10 h-full text-center text-sm bg-transparent border-none focus:ring-0 p-0 appearance-none remove-arrow"
              :min="minQuantity"
              max="10000"
              :step="stepQuantity"
              v-on:keyup.enter="addToCart($event)"
              v-model.number="productUnits"
            />
            <button
              @click="incUnits(product)"
              class="btn-qty plus w-6 h-full flex items-center justify-center hover:bg-gray-200 text-gray-600 rounded-r-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="16px"
                viewBox="0 -960 960 960"
                width="16px"
                fill="currentColor"
              >
                <path
                  d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"
                />
              </svg>
            </button>
          </div>
          <button
            v-if="canWriteOrders"
            @click="addToCart($event)"
            :disabled="loading || showPvpConsultar"
            class="btn-wishlist-action border border-border rounded-md bg-surface flex items-center justify-center gap-2 hover:bg-primary hover:text-white px-4 py-1.5 transition-colors duration-200 font-medium text-sm group/btn disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-surface disabled:hover:text-text-main"
            :title="showPvpConsultar ? 'PVP a consultar' : transPeralbaPro.item1277"
          >
            <x-icons
              name="add_shopping_cart"
              class="text-[18px] group-hover/btn:scale-110 transition-transform"
            />
            <span>{{ transPeralbaPro.item1277 }}</span>
          </button>
        </div>
      </div>
    </div>
    <div class="md:hidden flex items-center justify-end gap-2">
      <div
        class="input-quantity-div flex items-center border border-border rounded-md bg-surface h-11"
      >
        <button
          @click="decUnits(product)"
          :class="
            productUnits > minQuantity ? 'text-gray-600' : 'text-gray-300'
          "
          class="btn-qty minus w-8 h-full flex items-center justify-center hover:bg-gray-200 rounded-l-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="16px"
            viewBox="0 -960 960 960"
            width="16px"
            fill="currentColor"
          >
            <path d="M200-440v-80h560v80H200Z" />
          </svg>
        </button>
        <input
          :class="product.minimum > 0 ? 'only-decimal' : 'only-number'"
          type="number"
          class="input-quantity [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none w-10 h-full text-center text-base bg-transparent border-none focus:ring-0 p-0 appearance-none remove-arrow"
          :min="minQuantity"
          max="10000"
          :step="stepQuantity"
          v-on:keyup.enter="addToCart($event)"
          v-model.number="productUnits"
        />
        <button
          @click="incUnits(product)"
          class="btn-qty plus w-8 h-full flex items-center justify-center hover:bg-gray-200 text-gray-600 rounded-r-md"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="16px"
            viewBox="0 -960 960 960"
            width="16px"
            fill="currentColor"
          >
            <path
              d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z"
            />
          </svg>
        </button>
      </div>
      <button
        v-if="canWriteOrders"
        @click="addToCart($event)"
        :disabled="loading || showPvpConsultar"
        class="btn-wishlist-action border border-border rounded-md bg-surface flex items-center justify-center gap-2 hover:bg-primary hover:text-white px-6 py-2.5 transition-colors duration-200 font-medium text-md group/btn disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-surface disabled:hover:text-text-main"
        :title="showPvpConsultar ? 'PVP a consultar' : transPeralbaPro.item1277"
      >
        <x-icons
          name="add_shopping_cart"
          class="text-[18px] group-hover/btn:scale-110 transition-transform"
        />
        <span>{{ transPeralbaPro.item1277 }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import {hasNoSalePrice, materializeFegimeProduct} from "@/utils/fegime";

export default {
  props: [
    "product",
    "user",
    "item24",
    "item25",
    "item26",
    "template",
    "mobile",
    "warehouse",
    "transPeralbaPro",
    "locale",
    "canWriteOrders",
  ],
  name: "product-item",
  emits: ["ready"],

  data() {
    return {
      loading: false,
      productUnits: this.product.minimum > 0 ? this.product.minimum : 1,
      selectedVariant: null,
      localAttributes: [], // CLAVE: Copia local de los atributos
    };
  },

  watch: {
    "product.variants": {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.initializeVariants();
        }
      },
      immediate: true,
      deep: true,
    },
    selectedVariant: {
      handler(newVariant) {
        if (!newVariant) return;

        const min =
          newVariant.minimum_quantity > 0 ? newVariant.minimum_quantity : 1;

        this.productUnits = Number.parseInt(min);
      },
      deep: true,
    },
  },

  async mounted() {
    this.$nextTick(() => {
      this.$emit("ready");
    });
  },

  computed: {
    imageUrl() {
      if (!this.displayImage) {
        return "/front/img/product_images/peralba_default.png";
      }
      // Los productos creados desde Fegime guardan la URL absoluta de la
      // imagen remota: no hay que anteponer el host del PIM.
      if (String(this.displayImage).startsWith("http")) {
        return this.displayImage;
      }
      return "https://pim.peralba.com/" + this.displayImage;
    },

    hasVariants() {
      return (
        this.product.variants !== null && this.product.variants !== undefined
      );
    },

    // Articulo Fegime sin precio publicado (listas MatMax "sin precio" o sin
    // PVP en el catalogo): PVP a consultar. Tambien los ya materializados que
    // siguen sin precio, que llegan como un producto interno cualquiera.
    showPvpConsultar() {
      return hasNoSalePrice(this.product, this.currentPrice);
    },

    displayCode() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.erp_code;
      }
      return this.product.alias_code || this.product.erp_reference;
    },

    displayName() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.name;
      }
      return this.product.translation?.name || this.product.name;
    },

    displayStockStatus() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.available_stock?.status;
      }
      return this.product.available_stock?.status;
    },

    displayStockWeb() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.available_stock.stock_web;
      }
      return this.product.available_stock.stock_web;
    },

    currentLink() {
      if (this.hasVariants && this.selectedVariant) {
        return this.appendSearchTracking(this.selectedVariant.href);
      }
      return this.product.href;
    },

    displayImage() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.image;
      }
      return this.product.product_images[0]?.thumbnail;
    },

    currentId() {
      return this.hasVariants && this.selectedVariant
        ? this.selectedVariant.id
        : this.product.id;
    },

    currentPrice() {
      return this.hasVariants && this.selectedVariant
        ? this.selectedVariant.price
        : this.product.price;
    },

    currentDiscount() {
      return this.hasVariants && this.selectedVariant
        ? this.selectedVariant.discount
        : this.product.discount;
    },

    currentPriceWithoutDiscount() {
      return this.hasVariants && this.selectedVariant
        ? this.selectedVariant.price_without_discount
        : this.product.price_without_discount;
    },

    currentStock() {
      if (this.hasVariants && this.selectedVariant) {
        return {
          status: this.selectedVariant.stock > 0 ? "available" : "unavailable",
          text_ca:
            this.selectedVariant.stock > 0 ? "Disponible" : "No disponible",
          stock_web: this.selectedVariant.stock,
        };
      }
      return this.product.available_stock;
    },

    minQuantity() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.minimum_quantity > 0
          ? this.selectedVariant.minimum_quantity
          : 1;
      }
      return this.product.minimum > 0 ? this.product.minimum : 1;
    },

    stepQuantity() {
      if (this.hasVariants && this.selectedVariant) {
        return this.selectedVariant.minimum_quantity > 0
          ? this.selectedVariant.minimum_quantity
          : 1;
      }
      return this.product.minimum > 0 ? this.product.minimum : 1;
    },

    totalProductVariations() {
      if (!this.hasVariants) return 0;
      return this.localAttributes.reduce((total, attr) => {
        return total + attr.options.length;
      }, 0);
    },

    variationLabel() {
      const count = this.totalProductVariations;
      if (count === 2) return "+1";
      if (count >= 3 && count <= 5) return "+2";
      if (count >= 6 && count <= 10) return "+5";
      if (count >= 11 && count <= 20) return "+10";
      if (count >= 21) return "+20";
      return "";
    },

    // View transition card -> ficha: nombre estable por producto/variante
    // compartido con la imagen principal de la ficha (vt-<erp>). Debe ser un
    // custom-ident valido y unico en la pagina, de ahi el saneado y usar la
    // referencia (nexo PIM-Web) en vez de ids numericos. El alias_code manda
    // sobre el erp_reference porque la ficha ($vtName) y el erp_code de las
    // variantes se construyen igual (alias_code ?? erp_reference).
    vtStyle() {
      const ref =
        this.hasVariants && this.selectedVariant
          ? this.selectedVariant.erp_code
          : this.product.alias_code || this.product.erp_reference;
      if (!ref) return {};
      return {
        "view-transition-name":
          "vt-" + String(ref).replace(/[^A-Za-z0-9_-]/g, "-"),
        "view-transition-class": "vt-product-image",
      };
    },
  },

  methods: {
    // Antes eran `filters` locales (Vue 2), que tapaban a los filtros globales
    // del mismo nombre; en Vue 3 no hay filtros y la plantilla los llama como
    // metodos. Se conservan tal cual estaban aqui (sin las guardas de NaN de
    // utils/filters.js, que era la version global y nunca se aplicaba a este
    // componente).
    currency(value) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    },

    percentage(value) {
      if (parseInt(value) == parseFloat(value)) {
        return parseInt(value) + "%";
      } else {
        return parseFloat(value).toFixed(2) + "%";
      }
    },

    // Registra el click sobre el resultado cuando el usuario anade al carrito
    // desde el listado sin entrar a la ficha (donde normalmente se registra).
    // Propaga los parametros de tracking (slog/spos o sq/spos) del enlace del
    // producto. Fire-and-forget: nunca bloquea ni rompe el flujo del carrito.
    registerSearchClick() {
      try {
        const link = this.currentLink;
        if (!link) return;
        const query = link.split("?")[1];
        if (!query) return;
        const params = new URLSearchParams(query);
        if (!params.has("slog") && !params.has("sq")) return;

        const body = new URLSearchParams();
        if (this.product.is_fegime) {
          // Sin product_id: el click se registra por el codigo Fegime.
          if (!this.product.fegime_code) return;
          body.set("fegime_code", this.product.fegime_code);
        } else {
          body.set("product_id", this.currentId);
        }
        ["slog", "spos", "sq"].forEach((key) => {
          if (params.has(key)) body.set(key, params.get(key));
        });

        const csrf = document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content");

        fetch("/search-log/click", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-TOKEN": csrf,
            Accept: "application/json",
          },
          body: body.toString(),
          keepalive: true,
        }).catch(() => {});
      } catch (e) {
        /* no rompemos el add-to-cart por un fallo de tracking */
      }
    },

    // El href del producto del listado llega del servidor con los parametros de
    // tracking de busqueda (slog/spos); los href de las variantes vienen de la
    // api de variantes sin ellos, asi que se propagan al enlace de la variante.
    appendSearchTracking(href) {
      if (!href || !this.product.href) return href;
      const query = this.product.href.split("?")[1];
      if (!query) return href;
      const params = new URLSearchParams(query);
      const tracking = new URLSearchParams();
      ["slog", "sq", "spos"].forEach((key) => {
        if (params.has(key)) tracking.set(key, params.get(key));
      });
      const trackingQuery = tracking.toString();
      if (!trackingQuery) return href;
      return href + (href.includes("?") ? "&" : "?") + trackingQuery;
    },

    findClosestVariant(changedAttributeId, selectedOption) {
      // obtenir tots els candidats que contenen el valor seleccionat per l'atribut canviat
      const candidates = this.product.variants.matrix.filter((item) => {
        return item.characteristics.some(
          (char) =>
            char.attribute_id === changedAttributeId &&
            char.value === selectedOption.value,
        );
      });

      if (!candidates || candidates.length === 0) return null;

      // crear un mapa d'índexs per a cada atribut (segons l'ordre de options)
      const indexMap = {};
      this.localAttributes.forEach((attr) => {
        indexMap[attr.id] = {};
        attr.options.forEach((opt, idx) => {
          indexMap[attr.id][opt.value] = idx;
        });
      });

      // crear array amb índexs actuals (posició de la selecció actual per cada atribut)
      const currentIndexes = {};
      this.localAttributes.forEach((attr) => {
        // si l'atribut és l'que canviem, usem l'option passada (selectedOption)
        if (attr.id === changedAttributeId) {
          const idx = attr.options.findIndex(
            (o) => o.value === selectedOption.value,
          );
          currentIndexes[attr.id] = idx >= 0 ? idx : 0;
        } else {
          const selVal = attr.selected?.value;
          const idx = selVal ? indexMap[attr.id][selVal] ?? -1 : -1;
          currentIndexes[attr.id] = idx >= 0 ? idx : 0;
        }
      });

      // per cada candidat, calculem la "distància" respecte a les seleccions actuals
      const scored = candidates.map((candidate) => {
        let score = 0;
        // per cada atribut calculem diferència absoluta d'índexs
        this.localAttributes.forEach((attr) => {
          const char = candidate.characteristics.find(
            (c) => c.attribute_id === attr.id,
          );
          const candidateVal = char ? char.value : null;
          const candIdx = candidateVal
            ? indexMap[attr.id][candidateVal] ?? null
            : null;
          const curIdx = currentIndexes[attr.id];
          if (candIdx === null || candIdx === undefined) {
            // penalització alta si el candidat no defineix l'atribut
            score += 1000;
          } else {
            score += Math.abs(curIdx - candIdx);
          }
        });

        // opcional: afegir petit factor per prioritzar variants disponibles (més estoc)
        const stockPenalty = candidate.stock && candidate.stock <= 0 ? 50 : 0;
        score += stockPenalty;

        return {candidate, score};
      });

      // triar el candidat amb menor score
      scored.sort((a, b) => a.score - b.score);

      // retornar l'objecte variant del millor candidat (o null si no convé)
      return scored.length ? scored[0].candidate : null;
    },

    initializeVariants() {
      if (!this.hasVariants) return;

      // 1. Clonar profundamente en una variable temporal
      let attributes = JSON.parse(
        JSON.stringify(this.product.variants.attributes),
      );

      // 2. Ordenar los atributos alfabéticamente por 'name'
      attributes.sort((a, b) => {
        const textA = a.name || "";
        const textB = b.name || "";
        return textA.toString().localeCompare(textB.toString());
      });

      // Establecer la variante actual
      this.selectedVariant = {...this.product.variants.current};
      const currentCode = this.selectedVariant.code;

      // 3. Iterar para ordenar las opciones y preseleccionar
      attributes.forEach((attr) => {
        // Ordenar las opciones internamente alfabéticamente por 'value' (CORREGIDO)
        if (attr.options && attr.options.length) {
          attr.options.sort((a, b) => {
            const textA = a.value || "";
            const textB = b.value || "";
            return textA.toString().localeCompare(textB.toString());
          });
        }

        // Preseleccionar las opciones
        const selectedOption = attr.options.find(
          (opt) =>
            opt.available_products &&
            opt.available_products.includes(currentCode),
        );

        if (selectedOption) {
          attr.selected = {...selectedOption};
        }
      });

      // 4. Asignar al estado reactivo final
      this.localAttributes = attributes;
    },

    onAttributeChange(attribute, selectedOption) {
      // 1) trobar i actualitzar la selecció a la còpia local
      const attrIndex = this.localAttributes.findIndex(
        (a) => a.id === attribute.id,
      );
      if (attrIndex === -1) return;
      this.localAttributes[attrIndex].selected = {...selectedOption};

      // 2) construir llista de seleccions actuals (valor per atribut)
      const selectedAttributes = this.localAttributes
        .filter((attr) => attr.selected)
        .map((attr) => ({
          attribute_id: attr.id,
          value: attr.selected.value,
        }));

      // 3) intentar trobar una coincidència exacta a la matriu
      let matchingProduct = this.product.variants.matrix.find((item) => {
        return selectedAttributes.every((selected) => {
          return item.characteristics.some(
            (char) =>
              char.attribute_id === selected.attribute_id &&
              char.value === selected.value,
          );
        });
      });

      if (matchingProduct) {
        // actualitzar la variant seleccionada amb la coincidència exacta
        this.selectedVariant = {
          id: matchingProduct.id,
          code: matchingProduct.code,
          erp_code: matchingProduct.erp_code,
          name: matchingProduct.name,
          image: matchingProduct.image,
          href: matchingProduct.href,
          minimum_quantity: matchingProduct.minimum_quantity,
          price: matchingProduct.price,
          discount: matchingProduct.discount,
          price_without_discount: matchingProduct.price_without_discount,
          available_stock: matchingProduct.available_stock,
          characteristics: matchingProduct.characteristics,
        };

        // sincronitzar les seleccions locals per reflectir la variant
        this.localAttributes.forEach((attr, i) => {
          const char = matchingProduct.characteristics.find(
            (c) => c.attribute_id === attr.id,
          );
          if (char) {
            const opt = attr.options.find((o) => o.value === char.value);
            if (opt) this.localAttributes[i].selected = {...opt};
          }
        });

        this.adjustOtherAttributes(attribute.id, matchingProduct.code);
        return;
      }

      // 4) si no hi ha coincidència exacta: buscar la millor variant que contingui
      //    la selecció del atribut canviat i que sigui "més pròxima" a les altres seleccions
      const closest = this.findClosestVariant(attribute.id, selectedOption);
      if (closest) {
        // actualitzar selectedVariant i localAttributes segons la variant triada
        this.selectedVariant = {
          id: closest.id,
          code: closest.code,
          erp_code: closest.erp_code,
          name: closest.name,
          image: closest.image,
          href: closest.href,
          minimum_quantity: closest.minimum_quantity,
          price: closest.price,
          discount: closest.discount,
          price_without_discount: closest.price_without_discount,
          available_stock: closest.available_stock,
          characteristics: closest.characteristics,
        };

        this.localAttributes.forEach((attr, i) => {
          const char = closest.characteristics.find(
            (c) => c.attribute_id === attr.id,
          );
          if (char) {
            const opt = attr.options.find((o) => o.value === char.value);
            if (opt) this.localAttributes[i].selected = {...opt};
          }
        });

        this.adjustOtherAttributes(attribute.id, closest.code);
        return;
      }
    },

    adjustOtherAttributes(changedAttributeId, selectedCode) {
      // Para cada atributo que NO es el que cambió
      this.localAttributes.forEach((attr, index) => {
        if (attr.id !== changedAttributeId) {
          // Verificar si la opción actual está disponible con el nuevo producto
          if (
            attr.selected &&
            !attr.selected.available_products.includes(selectedCode)
          ) {
            // Buscar una opción compatible
            const compatibleOption = attr.options.find((opt) =>
              opt.available_products.includes(selectedCode),
            );

            if (compatibleOption) {
              this.localAttributes[index].selected = {...compatibleOption};
            }
          }
        }
      });
    },

    handleWishlist() {
      if (!window.isUserLoggedIn) {
        window.dispatchEvent(new CustomEvent("open-login-modal"));
        return;
      }

      if (typeof window.openWishlistModal === "function") {
        const qty = this.productUnits || this.minQuantity;

        // Articulo Fegime: sin id hasta materializar el producto.
        if (this.product.is_fegime) {
          materializeFegimeProduct(this.product).then((productId) => {
            if (productId) window.openWishlistModal(productId, qty);
          });
          return;
        }

        window.openWishlistModal(this.currentId, qty);
        return;
      }

      console.error("La función global no se ha cargado");
    },

    openModalClickAndCollect(event) {
      event.preventDefault();
      const modalElement = document.getElementById("modal-click-and-collect");
      if (modalElement) {
        const bootstrapModal = new bootstrap.Modal(modalElement);
        bootstrapModal.show();
      }
    },

    setDefaultImage(event) {
      event.target.src = "/front/img/brand/default-brand.png";
    },

    getStep() {
      const min = parseFloat(this.stepQuantity);
      return !isNaN(min) && min > 0 ? min : 1;
    },

    updateUnits(direction) {
      const step = this.getStep();
      let current = parseFloat(this.productUnits);

      if (!this.productUnits || isNaN(current) || current === 0) {
        this.productUnits = step;
        return;
      }

      const remainder = current % step;

      if (direction === "inc") {
        const addAmount = remainder !== 0 ? step - remainder : step;
        this.productUnits = current + addAmount;
      } else {
        const subAmount = remainder !== 0 ? remainder : step;
        const newValue = current - subAmount;
        this.productUnits = Math.max(newValue, step);
      }
    },

    incUnits(item) {
      this.updateUnits("inc");
    },

    decUnits(item) {
      this.updateUnits("dec");
    },

    // Alias: el template 0 (lista) llama addToShoppingCart.
    addToShoppingCart(event) {
      this.addToCart(event);
    },

    addToCart() {
      // "PVP a consultar": sin precio no se puede pedir por la web (la linea
      // iria a 0 €). El boton ya sale deshabilitado; esto cubre el enter del
      // input de cantidad y cualquier llamada suelta.
      if (this.showPvpConsultar) {
        return;
      }

      const qty = this.productUnits || this.minQuantity;

      // Contabiliza el click del resultado aunque el usuario no entre a la ficha.
      this.registerSearchClick();

      // Articulo Fegime: se materializa como producto web y sigue el flujo
      // estandar del modal con el id real.
      if (this.product.is_fegime) {
        materializeFegimeProduct(this.product).then((productId) => {
          if (!productId) return;

          window.openAddCartModal(productId, qty, {
            image: this.imageUrl,
            name: this.displayName,
            ref: this.displayCode,
            ...(!this.product.no_price && {pvp: this.currentPrice}),
          });
        });
        return;
      }

      const hasDiscount = parseFloat(this.currentDiscount) > 0;
      const productInfo = {
        image: this.imageUrl,
        name: this.displayName,
        ref: this.displayCode,
        ...(hasDiscount && {
          desc: this.currentDiscount,
          net: this.currentPrice,
          withoutdesc: this.currentPriceWithoutDiscount,
        }),
        ...(!hasDiscount && {
          pvp: this.currentPrice,
        }),
      };

      window.openAddCartModal(this.currentId, qty, productInfo);
    },
  },
};
</script>

<style scoped>
.truncate-2-lines {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Remove number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
